import React from 'react';
import LegalPage, { LEGAL_BRAND, LEGAL_COMPANY, LEGAL_CONTACT_EMAIL } from './LegalPage';

// ── Privacy Policy ────────────────────────────────────────────────────────────
// Public at /privacy. Written to what the platform actually does; the SMS/mobile section carries the wording the
// carriers' 10DLC registry requires, word for word (Telnyx "10DLC Privacy Policy" and "Campaign Compliance
// Requirements", read 2026-09-29). Change the facts here only when the practice changes.
const EFFECTIVE = 'September 29, 2026';

export default function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" effective={EFFECTIVE} current="privacy">
      <p>
        This Privacy Policy explains how {LEGAL_COMPANY} (“{LEGAL_BRAND}”, “we”, “us”) collects, uses and protects
        information when you use the {LEGAL_BRAND} platform, our websites, and the {LEGAL_BRAND} text-messaging program.
        By using {LEGAL_BRAND}, you agree to the practices described here.
      </p>

      <h2>1. Information we collect</h2>
      <h3>Information you provide</h3>
      <ul>
        <li><strong>Account and contact information</strong> — such as your name, email address and password (stored only in protected form), and, if you choose to give it, your mobile phone number.</li>
        <li><strong>Content you add to the platform</strong> — projects, documents, files you upload, and instructions or messages you give the platform’s AI features.</li>
        <li><strong>Communications with us</strong> — support requests and other messages you send us, including by email and by text message.</li>
      </ul>
      <h3>SMS / MMS information</h3>
      <ul>
        <li>Your mobile phone number and your consent to receive text messages, including when and how you gave it.</li>
        <li>The messages exchanged with the {LEGAL_BRAND} program, including any photos or media you send us, and delivery information such as timestamps and delivery status reported by our messaging provider.</li>
        <li>Keyword replies you send (for example STOP, START, HELP), which we use to honor your choices.</li>
      </ul>
      <h3>Service and technical information</h3>
      <ul>
        <li>Standard log information when you use the platform, such as your IP address, browser and device type, pages requested and the time of requests, which we use to run, secure and troubleshoot the service.</li>
        <li>Cookies and similar identifiers that keep you signed in and protect your session. We do not use advertising cookies.</li>
      </ul>

      <h2>2. How we use information</h2>
      <ul>
        <li>To provide, operate and secure the {LEGAL_BRAND} platform and your account.</li>
        <li>To send you the text messages you have opted in to receive: account notifications, customer-care communications, and authentication or verification codes (see section 4).</li>
        <li>To respond to your requests and provide support.</li>
        <li>To understand how the service is used so we can maintain and improve it.</li>
        <li>To comply with law and enforce our <a href="/terms">Terms &amp; Conditions</a>.</li>
      </ul>
      <p>We do not sell your personal information.</p>

      <h2>3. Service providers</h2>
      <p>
        We use service providers that process information on our behalf and only for the purposes above, such as
        hosting and infrastructure providers, our text-messaging carrier and messaging provider, and AI model providers
        that process the content you give the platform’s AI features in order to generate responses. If and when{' '}
        {LEGAL_BRAND} offers paid features, payment processing is handled by a payment processor and we do not store full
        payment card numbers. We may also disclose information when required by law, to protect the rights, safety and
        security of {LEGAL_BRAND} and its users, or as part of a business transfer.
      </p>

      <h2>4. Text messaging (SMS / MMS) and mobile information</h2>
      <div className="legal-callout">
        <p>Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.</p>
        <p>All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</p>
        <p>We will not share your opt-in to an SMS campaign with any third party for purposes unrelated to providing you with the services of that campaign.</p>
      </div>
      <p>
        The {LEGAL_BRAND} messaging program sends account notifications, customer-care communications and authentication
        (two-factor) codes to people who provide their mobile number through a {LEGAL_BRAND} application and explicitly
        consent to receive those messages. Consent to receive text messages is not a condition of any purchase. Message
        frequency varies. Message and data rates may apply. You can opt out at any time by replying <strong>STOP</strong>{' '}
        (or <strong>UNSUBSCRIBE</strong>), and get help by replying <strong>HELP</strong> or emailing{' '}
        <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>. The full program terms are in our{' '}
        <a href="/terms#sms">Terms &amp; Conditions</a>.
      </p>
      <p>
        Your mobile number, your consent record and the messages exchanged with the program are used only to deliver
        that program to you, to honor your opt-out and help requests, to keep a record that you consented, and to meet
        our obligations to the carriers. Our messaging provider processes messages to deliver them and is not permitted
        to use your information for its own purposes.
      </p>

      <h2>5. Retention</h2>
      <p>
        We keep information for as long as it is needed for the purposes described in this policy — for example, for as
        long as your account is active, for as long as needed to provide a service you asked for, and afterwards only as
        needed to keep required records (such as a record of your text-messaging consent and opt-out), resolve disputes
        and comply with our legal obligations. When information is no longer needed we delete it or make it anonymous.
      </p>

      <h2>6. Security</h2>
      <p>
        We protect information with technical and organizational measures appropriate to its sensitivity, including
        encryption in transit, protected credential storage, access controls and logging of access to systems that hold
        your information. No method of transmission or storage is completely secure, and we cannot guarantee absolute
        security.
      </p>

      <h2>7. Your choices and rights</h2>
      <ul>
        <li><strong>Text messages:</strong> reply STOP to any message to stop receiving messages from the program; reply START or YES to opt back in; reply HELP for help.</li>
        <li><strong>Account information:</strong> you can view and update your account details in the platform, or ask us to correct or delete information by emailing us.</li>
        <li><strong>Access and deletion:</strong> you may ask what information we hold about you and ask us to delete it, subject to records we are required to keep.</li>
        <li>Depending on where you live, you may have additional rights under applicable privacy laws. To exercise any right, contact us at the address below and we will respond as required by the laws that apply to you.</li>
      </ul>

      <h2>8. Children</h2>
      <p>
        {LEGAL_BRAND} is a business platform and is not directed to children under 13. We do not knowingly collect
        personal information from children under 13; if you believe a child has provided us information, contact us and
        we will delete it.
      </p>

      <h2>9. Changes to this policy</h2>
      <p>
        We may update this policy as the service changes. The effective date at the top shows when it was last revised;
        material changes will be posted here.
      </p>

      <h2>10. Contact us</h2>
      <dl className="legal-kv">
        <dt>Company</dt><dd>{LEGAL_COMPANY}</dd>
        <dt>Brand</dt><dd>{LEGAL_BRAND}</dd>
        <dt>Email</dt><dd><a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a></dd>
        <dt>Website</dt><dd><a href="/">www.3aimigos.com</a></dd>
      </dl>
    </LegalPage>
  );
}
